import { z } from "zod";

/** A recurring content format, e.g. "Answer autopsy": how pieces in it are shaped. */
export const pillarSchema = z.object({
  id: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9]+(?:[_-][a-z0-9]+)*$/, { message: "Use lower case, digits, _ or -" })
    .max(40),
  name: z.string().trim().min(2, "Give each pillar a name").max(60),
  format: z
    .string()
    .trim()
    .min(10, "Say how posts in each pillar are shaped, in a sentence")
    .max(600),
});
export type Pillar = z.infer<typeof pillarSchema>;

/** The platforms a brand can post to. Posting is by hand for now; each format ends in posting steps. */
export const PLATFORMS = {
  linkedin: { name: "LinkedIn", short: "LI" },
  instagram: { name: "Instagram", short: "IG" },
  tiktok: { name: "TikTok", short: "TT" },
  snapchat: { name: "Snapchat", short: "SC" },
  facebook: { name: "Facebook", short: "FB" },
  reddit: { name: "Reddit", short: "RD" },
  threads: { name: "Threads", short: "TH" },
  x: { name: "X", short: "X" },
} as const;
export type Platform = keyof typeof PLATFORMS;
export const platformSchema = z.enum(Object.keys(PLATFORMS) as [Platform, ...Platform[]]);

/** What can be made, as "channel:format": text from the writer, slides from the designer. */
export const CONTENT_FORMATS = {
  "linkedin:text": "LinkedIn post",
  "linkedin:carousel": "LinkedIn document (PDF carousel)",
  "instagram:carousel": "Instagram carousel",
  "tiktok:carousel": "TikTok photo carousel",
  "snapchat:carousel": "Snapchat story",
  "facebook:text": "Facebook post",
  "facebook:carousel": "Facebook carousel",
  "reddit:text": "Reddit answer (posted by a person)",
  "threads:text": "Threads post",
  "x:text": "X post or thread",
} as const;
export type ContentFormat = keyof typeof CONTENT_FORMATS;
export const contentFormatSchema = z.enum(
  Object.keys(CONTENT_FORMATS) as [ContentFormat, ...ContentFormat[]],
);
export const formatsOf = (platform: string) =>
  (Object.keys(CONTENT_FORMATS) as ContentFormat[]).filter((format) =>
    format.startsWith(`${platform}:`),
  );

/** One channel a brand posts to: which formats, how often, and who looks after it. */
export const brandChannelSchema = z
  .object({
    id: platformSchema,
    formats: z.array(contentFormatSchema).min(1).max(4),
    /** Posts a week the director plans for this channel. */
    perWeek: z.number().int().min(0).max(21),
    /** The member who approves and posts this channel's pieces (Today list). Null: anyone. */
    ownerId: z.string().nullable().default(null),
  })
  .refine((channel) => channel.formats.every((format) => format.startsWith(`${channel.id}:`)), {
    message: "Each format must belong to its channel",
    path: ["formats"],
  });
export type BrandChannel = z.infer<typeof brandChannelSchema>;

/**
 * How content is shaped for one brand (Brand.settings). The writer reads it on every run, next
 * to the brand's knowledge. Everything is optional, so a new brand works with empty settings.
 */
export const brandContentSchema = z.object({
  /** When false, drafts may not mention prices (e.g. before pricing launches). */
  pricingPublic: z.boolean().default(true),
  /** Spelling and register, e.g. "Australian English". */
  language: z.string().trim().max(80).default(""),
  /** Who LinkedIn posts are written as, e.g. "Abhishek, First Round's founder". */
  linkedinAuthor: z.string().trim().max(200).default(""),
  /** Where Reddit answers will be posted, e.g. "r/auscorp and Australian uni subreddits". */
  communities: z.string().trim().max(1000).default(""),
  /** Example hashtags for captions. */
  hashtags: z.string().trim().max(300).default(""),
  /** How to vary examples so a batch doesn't repeat itself. */
  exampleVariety: z.string().trim().max(1000).default(""),
  pillars: z.array(pillarSchema).max(20).default([]),
  /** Where the brand posts. The director plans only these; Today lists them by owner. */
  channels: z
    .array(brandChannelSchema)
    .max(8)
    .refine((list) => new Set(list.map((channel) => channel.id)).size === list.length, {
      message: "Each channel once",
    })
    .default([]),
});
export type BrandContent = z.infer<typeof brandContentSchema>;

/** Settings as stored; anything unreadable falls back to the defaults rather than failing a run. */
export const readBrandContent = (value: unknown): BrandContent => {
  const parsed = brandContentSchema.safeParse(value ?? {});
  return parsed.success ? parsed.data : brandContentSchema.parse({});
};

/** Channels the writer handles (text posts). */
export const textChannelSchema = z.enum(["linkedin", "reddit", "x", "threads", "facebook"]);
export type TextChannel = z.infer<typeof textChannelSchema>;

export const CHANNEL_CODES: Record<string, string> = {
  linkedin: "li",
  reddit: "rd",
  instagram: "ig",
  tiktok: "tt",
  snapchat: "sc",
  facebook: "fb",
  threads: "th",
  x: "x",
  youtube: "yt",
  blog: "bl",
};

export const pieceStatusSchema = z.enum([
  "drafting",
  "ready",
  "needs_human",
  "blocked",
  "awaiting_approval",
  "approved",
  "posted",
  "rejected",
  "failed",
]);
export type PieceStatus = z.infer<typeof pieceStatusSchema>;

export const PIECE_STATUS_LABELS: Record<PieceStatus, string> = {
  drafting: "Drafting",
  ready: "Ready",
  needs_human: "Needs you",
  blocked: "Blocked by claims check",
  awaiting_approval: "Waiting for approval",
  approved: "Approved: to post",
  posted: "Posted",
  rejected: "Rejected",
  failed: "Failed",
};
